import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';
import { Calendar, Users, Clock, Phone, MapPin, PartyPopper } from 'lucide-react';

const BookEvent = () => {
  const [pageContent, setPageContent] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch page content
        const pageRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/page-content/book-event`);
        if (pageRes.ok) {
          const data = await pageRes.json();
          if (data) {
            setPageContent(data);
          }
        }
        
        // Fetch site settings for phone/address
        const settingsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
        if (settingsRes.ok) {
          setSiteSettings(await settingsRes.json());
        }
      } catch (e) {
        console.log('Using default content');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper to get section by type
  const getSection = (type) => {
    return pageContent?.sections?.find(s => s.type === type && s.is_active !== false);
  };

  // Icon mapping for features
  const iconMap = {
    users: Users,
    clock: Clock,
    calendar: Calendar,
    party: PartyPopper
  };

  // Extract a YouTube video ID from any common URL format
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-4xl font-black">Loading...</div>
      </div>
    );
  }

  // Get phone and address from settings or use defaults
  const phoneNumber = siteSettings?.phone || '(940) 325-5904';
  const phoneLink = phoneNumber.replace(/\D/g, '');
  const address = siteSettings?.address || '115 NW 1st Ave, Mineral Wells, TX 76067';

  const heroSection = getSection('hero');
  const cardsSection = getSection('cards');
  const featuresSection = getSection('features');
  const ctaSection = getSection('cta');
  const gallerySection = getSection('gallery');
  const videoSection = getSection('video');

  return (
    <div 
      className="min-h-screen pt-32"
      style={{
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      }}
    >
      <SEO 
        title={pageContent?.page_title || "Book Your Event"}
        description={pageContent?.meta_description || "Host your next party or event at 1st Ave ICP in Mineral Wells, TX!"}
        keywords="book event Mineral Wells, birthday party ice cream, corporate event venue Texas, party venue Mineral Wells TX"
        canonical="/book-event"
      />

      {/* Hero Section */}
      {heroSection && (
        <section className="relative bg-gradient-to-br from-purple-500 to-pink-500 pt-16 pb-24 px-6">
          <div className="max-w-7xl mx-auto text-center text-white">
            <div className="flex justify-center mb-6">
              <PartyPopper className="w-16 h-16" />
            </div>
            <h1 
              className="text-4xl md:text-6xl font-bold mb-4"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              {heroSection.title}
            </h1>
            <p className="text-xl md:text-2xl opacity-90 max-w-2xl mx-auto">
              {heroSection.subtitle}
            </p>
          </div>
          
          {/* Wavy Bottom Edge */}
          <div className="absolute bottom-0 left-0 right-0">
            <svg className="w-full h-16" viewBox="0 0 1440 64" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path 
                fill="#ffffff" 
                d="M0,32 C360,64 720,0 1080,32 C1260,48 1380,48 1440,32 L1440,64 L0,64 Z"
              />
            </svg>
          </div>
        </section>
      )}

      {/* Event Types Cards Section */}
      {cardsSection && cardsSection.items?.length > 0 && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-bold text-center mb-12"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              {cardsSection.title}
            </h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {cardsSection.items.map((card, idx) => (
                <div 
                  key={idx}
                  className="bg-gradient-to-br from-pink-50 to-purple-50 p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all text-center"
                >
                  <div className="text-5xl mb-4">{card.icon}</div>
                  <h3 className="text-2xl font-black mb-3">{card.title}</h3>
                  <p className="text-gray-600 whitespace-pre-line">{card.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      {featuresSection && featuresSection.items?.length > 0 && (
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-6xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-bold text-center mb-12"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              {featuresSection.title}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              {featuresSection.items.map((feature, idx) => {
                const IconComponent = iconMap[feature.icon] || PartyPopper;
                return (
                  <div key={idx} className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-md">
                    <div className="bg-pink-100 p-3 rounded-full">
                      <IconComponent className="w-6 h-6 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                      <p className="text-gray-600 whitespace-pre-line">{feature.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Featured YouTube Video Section */}
      {videoSection && getYouTubeVideoId(videoSection.youtube_url) && (
        <section className="py-16 px-6 bg-white" data-testid="book-event-video-section">
          <div className="max-w-4xl mx-auto">
            {videoSection.title && (
              <h2
                className="text-3xl md:text-4xl font-bold text-center mb-8"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {videoSection.title}
              </h2>
            )}
            <div className="aspect-video rounded-2xl overflow-hidden shadow-xl">
              <iframe
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(videoSection.youtube_url)}`}
                title={videoSection.title || 'Featured video'}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                data-testid="book-event-featured-video"
              />
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {ctaSection && (
        <section className="py-16 px-6 bg-gradient-to-br from-[#E91E8C] to-[#C4197A]">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 
              className="text-3xl md:text-4xl font-bold mb-6"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              {ctaSection.title}
            </h2>
            <p className="text-xl mb-8 opacity-90">
              {ctaSection.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <a href={`tel:+1${phoneLink}`} className="flex items-center gap-2 bg-white text-pink-600 px-6 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors">
                <Phone className="w-5 h-5" />
                {phoneNumber}
              </a>
              <a 
                href="/contact" 
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-full font-bold transition-colors border-2 border-white"
              >
                Send Us a Message
              </a>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-white/80">
              <MapPin className="w-5 h-5" />
              <span>{address}</span>
            </div>
          </div>
        </section>
      )}

      {/* Gallery Section */}
      {gallerySection && gallerySection.items?.length > 0 && (
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-bold text-center mb-12"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              {gallerySection.title || 'Event Gallery'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {gallerySection.items.filter(it => it.url || it.youtube_url).map((item, idx) => {
                const ytId = item.youtube_url ? (item.youtube_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/) || [])[1] : null;
                const isVideoFile = item.is_video || /\.(mp4|mov|webm)$/i.test(item.url || '');
                return (
                  <div key={idx} className="aspect-square rounded-xl overflow-hidden shadow-lg bg-black" data-testid={`book-event-gallery-item-${idx}`}>
                    {ytId ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${ytId}`}
                        title={item.caption || `Event video ${idx + 1}`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : isVideoFile ? (
                      <video
                        src={item.url}
                        className="w-full h-full object-cover"
                        controls
                        playsInline
                        preload="metadata"
                        data-testid={`book-event-gallery-video-${idx}`}
                      />
                    ) : (
                      <OptimizedImage
                        src={item.url}
                        alt={item.caption || `Event photo ${idx + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                        width={300}
                        height={300}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Bottom Padding */}
      <div className="h-16 bg-white"></div>
    </div>
  );
};

export default BookEvent;
